import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { absoluteTime, relativeTime } from '../lib/format';
import { useSession } from '../lib/session';
import { useAgents, useGroups, usePriorities, useStatuses } from '../lib/directory';
import { Badge, Button, ErrorState, Separator, Skeleton } from '../components/ui';
import { PageHeader } from './shell';
import { AttachmentsPanel, useTicketAttachments } from '../features/tickets/attachments';
import { Composer } from '../features/tickets/composer';
import { useTicketFormFields } from '../features/tickets/custom-fields';
import { refreshTicket, useTicket, useTimeline } from '../features/tickets/detail-data';
import { MergedBanner } from '../features/tickets/detail-merge';
import { SlaChip, SlaTargets } from '../features/tickets/detail-sla';
import {
  LifecycleButton,
  ResolveDialog,
  StatusMenu,
  type ResolveTarget,
} from '../features/tickets/detail-status';
import { Timeline, type TimelineNames } from '../features/tickets/detail-timeline';
import { CustomFieldsCard, PropertiesPanel, RequesterFacts } from '../features/tickets/properties-panel';
import { ticketStatusTone } from '../features/tickets/status-tone';
import {
  LinksPanel,
  MergeControl,
  RequestedItemsPanel,
  TasksPanel,
  TimePanel,
  WatchersPanel,
} from '../features/tickets/ticket-panels';

/**
 * One ticket.
 *
 * The conversation comes first and the properties sit in a rail, because an
 * agent opening a ticket is reading before they are editing. Below the width
 * where two columns fit, the page becomes one scroll — conversation, reply box,
 * then the rail — rather than two fixed-height columns, which squeezed the
 * conversation to nothing on a tablet and left the rail unable to scroll.
 *
 * A requester sees the same page without the desk's machinery: their
 * conversation, a reply box, the status in their own words and a few plain
 * facts. No clocks, no internal notes, no assignment or merging.
 */
export function TicketDetailRoute() {
  const { number } = useParams<{ number: string }>();
  if (!number || !/^\d+$/.test(number)) return <NotFound backTo="/tickets" />;
  // Keyed by number, so everything held on screen — a draft, the reply/note
  // choice, files being uploaded, a half-typed task — belongs to one ticket.
  // Moving to another ticket used to keep the same component mounted, and a
  // reply typed on one could be sent, files and all, to the next.
  return <TicketDetailView key={number} number={number} />;
}

function useDocumentTitle(title: string) {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · Aurora`;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/** Names for the raw ids automatic changes record, so the timeline can say who and what. */
function useTimelineNames(enabled: boolean): TimelineNames {
  const agents = useAgents(enabled);
  const groups = useGroups(enabled);
  const statuses = useStatuses(enabled);
  const priorities = usePriorities(enabled);
  return useMemo(
    () => ({
      people: new Map((agents.data ?? []).map((agent) => [agent.id, agent.name])),
      groups: new Map((groups.data ?? []).map((group) => [group.id, group.name])),
      statuses: new Map((statuses.data ?? []).map((status) => [status.id, status.name])),
      priorities: new Map((priorities.data ?? []).map((priority) => [priority.id, priority.name])),
    }),
    [agents.data, groups.data, statuses.data, priorities.data],
  );
}

function TicketDetailView({ number }: { number: string }) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { session, can } = useSession();
  const isAgent = Boolean(session?.is_agent);
  const ticketQuery = useTicket(number);
  const timelineQuery = useTimeline(number);
  const attachmentsQuery = useTicketAttachments(number);
  const formFields = useTicketFormFields(Number(number));
  const names = useTimelineNames(isAgent);
  const [resolveTarget, setResolveTarget] = useState<ResolveTarget | null>(null);

  // Back to the list the agent came from, filters and page intact, when the
  // list said where that was; otherwise to the list.
  const from = (location.state as { from?: unknown } | null)?.from;
  const backTo = typeof from === 'string' && from.startsWith('/tickets') ? from : '/tickets';
  const backLabel = isAgent ? 'Tickets' : 'My requests';

  const ticket = ticketQuery.data?.data;
  useDocumentTitle(ticket ? `#${ticket.number} ${ticket.subject}` : `#${number}`);

  // Opening a ticket lands on its latest message, sending one brings it into
  // view, and it stays in view while the page settles around it. Only once the
  // conversation is actually on screen: the ticket, the session and the
  // timeline arrive separately, and scrolling while the skeleton was still up
  // scrolled nothing.
  const end = useRef<HTMLDivElement>(null);
  const { away: awayFromLatest, scrollToLatest } = useFollowLatest(
    end,
    Boolean(ticket && session && timelineQuery.data),
  );

  // Not drawn until it is known whose view this is: drawing the requester's
  // version first and swapping in the desk's a moment later is a flash of the
  // wrong screen.
  if (ticketQuery.isLoading || !session) return <DetailSkeleton />;

  if (ticketQuery.isError || !ticket) {
    const notFound = ticketQuery.error?.status === 404;
    return notFound ? (
      <NotFound backTo={backTo} label={backLabel} />
    ) : (
      <div className="px-4 py-4 sm:px-6">
        <ErrorState
          title="This ticket could not be loaded"
          message={ticketQuery.error?.message ?? 'Something went wrong.'}
          requestId={ticketQuery.error?.requestId}
          onRetry={() => void ticketQuery.refetch()}
        />
      </div>
    );
  }

  const fields = formFields.data?.data ?? [];
  const meta = formFields.data?.meta;
  const reopenWindowDays = meta?.reopen_window_days ?? 14;
  const editable = isAgent && can('ticket.edit');
  const merged = Boolean(ticket.merged_into);
  const canReply = can('ticket.reply') && !merged;

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={ticket.subject}
        back={{ to: backTo, label: backLabel }}
        badges={
          isAgent ? (
            <>
              <StatusMenu ticket={ticket} editable={editable && !merged} onResolve={setResolveTarget} />
              <SlaChip ticket={ticket} />
              {ticket.is_escalated && <Badge tone="critical">Escalated</Badge>}
            </>
          ) : (
            <Badge tone={ticketStatusTone(ticket.status.category)} dot>
              {ticket.status.requester_label ?? ticket.status.label}
            </Badge>
          )
        }
        description={
          <>
            <span className="font-mono">#{ticket.number}</span>
            {' · '}
            {isAgent && ticket.requester ? (
              <>
                Raised by <bdi className="font-medium text-ink">{ticket.requester.name}</bdi>
                {ticket.requester.is_vip && (
                  <Badge tone="caution" className="ms-1 align-middle">
                    VIP
                  </Badge>
                )}{' '}
              </>
            ) : (
              'Raised '
            )}
            <time dateTime={ticket.created_at} title={absoluteTime(ticket.created_at)}>
              {relativeTime(ticket.created_at)}
            </time>
          </>
        }
        actions={editable && !merged ? <LifecycleButton ticket={ticket} onResolve={setResolveTarget} /> : undefined}
      />

      <MergedBanner ticket={ticket} />

      <div className="scrollbar-slim min-h-0 flex-1 overflow-y-auto lg:flex lg:overflow-hidden">
        {/* --- Conversation and reply ---------------------------------- */}
        <div className="lg:flex lg:min-w-0 lg:flex-1 lg:flex-col">
          <div className="scrollbar-slim relative lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
            <div className="mx-auto max-w-3xl px-4 py-4 sm:px-6">
              {timelineQuery.isLoading ? (
                <div className="space-y-3" aria-busy>
                  <Skeleton className="h-28 w-full" />
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="h-24 w-full" />
                </div>
              ) : timelineQuery.isError ? (
                <ErrorState
                  title="The conversation could not be loaded"
                  message={timelineQuery.error.message}
                  requestId={timelineQuery.error.requestId}
                  onRetry={() => void timelineQuery.refetch()}
                />
              ) : (
                <Timeline
                  entries={timelineQuery.data?.data ?? []}
                  attachments={Array.isArray(attachmentsQuery.data?.data) ? attachmentsQuery.data!.data : []}
                  names={names}
                  isAgent={isAgent}
                />
              )}
              <div ref={end} />
            </div>
            {awayFromLatest && (
              <div className="pointer-events-none sticky bottom-3 hidden justify-center lg:flex">
                <Button size="sm" className="pointer-events-auto shadow-float" onClick={() => scrollToLatest(true)}>
                  <ArrowDown aria-hidden className="size-3.5" />
                  Jump to latest
                </Button>
              </div>
            )}
          </div>

          {canReply && (
            <div className="border-t border-line bg-canvas px-4 py-3 sm:px-6">
              <div className="mx-auto max-w-3xl">
                <Composer ticket={ticket} reopenWindowDays={reopenWindowDays} onSent={() => scrollToLatest()} />
              </div>
            </div>
          )}
        </div>

        {/* --- The rail ------------------------------------------------- */}
        <aside
          aria-label="Ticket details"
          className="scrollbar-slim space-y-4 border-t border-line bg-surface px-4 py-4 sm:px-6 lg:w-80 lg:shrink-0 lg:overflow-y-auto lg:border-s lg:border-t-0 lg:px-4 xl:w-96"
        >
          {isAgent ? (
            <>
              <PropertiesPanel ticket={ticket} locked={merged} />
              <Separator />
              <SlaTargets ticket={ticket} />
              <Separator />
              <CustomFieldsCard ticket={ticket} fields={fields} loading={formFields.isLoading} locked={merged} />
              <RequestedItemsPanel number={number} />
              <AttachmentsPanel number={number} />
              <TasksPanel number={number} />
              <TimePanel number={number} />
              <LinksPanel number={number} />
              <WatchersPanel number={number} />
              {!merged && (
                <MergeControl number={number} onMerged={() => refreshTicket(queryClient, number, ['watchers', 'links'])} />
              )}
            </>
          ) : (
            <>
              <RequesterFacts ticket={ticket} />
              <CustomFieldsCard ticket={ticket} fields={fields} loading={formFields.isLoading} />
              <RequestedItemsPanel number={number} />
              <AttachmentsPanel number={number} />
            </>
          )}
        </aside>
      </div>

      {isAgent && (
        <ResolveDialog
          ticket={ticket}
          target={resolveTarget}
          onClose={() => setResolveTarget(null)}
          fields={fields}
          requireNote={meta?.require_resolution_note ?? false}
          reopenWindowDays={reopenWindowDays}
        />
      )}
    </div>
  );
}

/**
 * Keeps the newest message in view.
 *
 * Scrolling to the end once was not enough. The reply box, the names automatic
 * changes are told in, and any images all arrive a moment after the
 * conversation does, and each pushes its end further down: measured on a
 * 768px-tall screen, a ticket "opened at its latest message" stopped 70px short
 * of it, and a reply sent a moment before the refetch landed out of sight below
 * the fold. So while the end of the conversation is in view it is kept in view,
 * whatever grows above or around it — until the person scrolls away from it,
 * which is what "Jump to latest" brings them back from.
 */
function useFollowLatest(end: RefObject<HTMLElement | null>, ready: boolean) {
  const [away, setAway] = useState(false);
  const following = useRef(true);
  // Which box scrolls changes at the two-column width, so the watch is set up again.
  const wide = useMediaQuery('(min-width: 1024px)');

  const scrollToLatest = useCallback(
    (smooth = false) => {
      following.current = true;
      end.current?.scrollIntoView?.({ block: 'nearest', behavior: smooth ? 'smooth' : 'auto' });
    },
    [end],
  );

  useEffect(() => {
    const marker = end.current;
    if (!ready || !marker) return;
    const container = scrollContainerOf(marker);
    scrollToLatest();

    const intersection =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(
            ([entry]) => {
              const visible = entry?.isIntersecting ?? true;
              following.current = visible;
              setAway(!visible);
            },
            { root: container, threshold: 0 },
          );
    intersection?.observe(marker);

    // Anything that changes size — the conversation, the reply box beside it,
    // the box that scrolls — while the end is in view keeps it there.
    const resize =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            if (following.current) marker.scrollIntoView?.({ block: 'nearest' });
          });
    const watched = new Set<Element>();
    if (marker.parentElement) watched.add(marker.parentElement);
    if (container) {
      watched.add(container);
      for (const child of Array.from(container.children)) watched.add(child);
    }
    for (const element of watched) resize?.observe(element);

    return () => {
      intersection?.disconnect();
      resize?.disconnect();
    };
  }, [ready, wide, end, scrollToLatest]);

  return { away, scrollToLatest };
}

/** The nearest ancestor that scrolls: the conversation's own column when there are two, the page when there is one. */
function scrollContainerOf(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
}

function useMediaQuery(query: string): boolean {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [matches, setMatches] = useState(() => supported && window.matchMedia(query).matches);
  useEffect(() => {
    if (!supported) return;
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener?.('change', update);
    return () => list.removeEventListener?.('change', update);
  }, [query, supported]);
  return matches;
}

/** A ticket that is not there, with the way back — retrying a 404 helps nobody. */
function NotFound({ backTo, label = 'Tickets' }: { backTo: string; label?: string }) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <p className="text-sm font-medium text-ink">Ticket not found</p>
      <p className="mt-1 max-w-md text-body text-ink-muted">
        It may have been deleted, or you may not have access to it.
      </p>
      <Button asChild variant="secondary" className="mt-4">
        <Link to={backTo}>
          <ArrowLeft aria-hidden className="size-3.5 rtl:rotate-180" />
          Back to {label.toLowerCase()}
        </Link>
      </Button>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="flex h-full flex-col" aria-busy aria-label="Loading ticket">
      <div className="shrink-0 space-y-2 border-b border-line bg-surface px-4 py-3 sm:px-6">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
      </div>
      <div className="min-h-0 flex-1 lg:flex">
        <div className="mx-auto w-full max-w-3xl space-y-3 px-4 py-4 sm:px-6 lg:flex-1">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-24 w-full" />
        </div>
        <div className="hidden space-y-3 border-s border-line px-4 py-4 lg:block lg:w-80 xl:w-96">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-6 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}
